import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  UserIcon,
  BellIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';

import { API_BASE_URL } from '../../../../config/api';

// Enum types to match backend
type FacilityCategory = 'studyroom' | 'academy' | 'coworking' | 'library';
type FacilityApprovalStatus = 'pending' | 'approved' | 'rejected' | 'suspended';
type FacilityOperationStatus = 'active' | 'inactive' | 'maintenance' | 'closed';

interface Organization {
  id: number;
  name: string;
  business_type: string;
  plan_type: string;
  status: string;
}

interface Facility {
  id: number;
  facility_name: string;
  organization_id: number;
  category: FacilityCategory;
  address: string;
  district?: string;
  city?: string;
  postal_code?: string;
  latitude?: number;
  longitude?: number;
  phone?: string;
  email?: string;
  website?: string;
  total_seats: number;
  operating_hours?: any;
  amenities?: any;
  approval_status: FacilityApprovalStatus;
  operation_status: FacilityOperationStatus;
  approved_by?: number;
  approved_at?: string;
  rejection_reason?: string;
  monthly_revenue: number;
  total_users: number;
  current_occupancy: number;
  last_activity?: string;
  notes?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
  organization?: Organization;
}

interface FacilityListResponse {
  facilities: Facility[];
  total: number;
  page: number;
  limit: number;
  total_pages: number;
}

interface DashboardStats {
  total_facilities: number;
  approval_stats: { [key: string]: number };
  operation_stats: { [key: string]: number };
  category_stats: { [key: string]: number };
  region_stats: Array<{ city: string; count: number }>;
  monthly_revenue: number;
  pending_count: number;
  recent_activities: Array<{
    id: number;
    facility_id: number;
    action: string;
    admin_id: number;
    reason?: string;
    created_at: string;
  }>;
  pending_facilities: Array<{
    id: number;
    facility_name: string;
    category: string;
    city?: string;
    created_at: string;
  }>;
}

const FacilityManagement: React.FC = () => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject' | 'suspend' | 'resume' | null>(null);
  const [actionReason, setActionReason] = useState('');

  // 필터링 및 검색 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState<FacilityApprovalStatus | ''>('');
  const [operationStatusFilter, setOperationStatusFilter] = useState<FacilityOperationStatus | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<FacilityCategory | ''>('');
  const [cityFilter, setCityFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // 통계 정보
  const [stats, setStats] = useState<DashboardStats | null>(null);

  useEffect(() => {
    fetchFacilities();
    fetchDashboardStats();
  }, [currentPage, searchTerm, approvalStatusFilter, operationStatusFilter, categoryFilter, cityFilter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  const fetchFacilities = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
        sort_by: 'created_at',
        sort_order: 'desc',
        ...(searchTerm && { search: searchTerm }),
        ...(approvalStatusFilter && { approval_status: approvalStatusFilter }),
        ...(operationStatusFilter && { operation_status: operationStatusFilter }),
        ...(categoryFilter && { category: categoryFilter }),
        ...(cityFilter && { city: cityFilter }),
      });

      const response = await fetch(`${API_BASE_URL}/admin/facilities?${params}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: FacilityListResponse = await response.json();
      setFacilities(data.facilities);
      setTotalCount(data.total);
    } catch (err) {
      console.error('Failed to fetch facilities:', err);
      setError(err instanceof Error ? err.message : '시설 목록 조회 실패');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/facilities/dashboard/stats`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: DashboardStats = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
    }
  };

  const handleFacilityAction = async (action: 'approve' | 'reject' | 'suspend' | 'resume', facilityId: number, reason?: string) => {
    try {
      const body = {
        ...(reason && { reason }),
        ...(action === 'approve' && { notes: reason }),
      };

      const response = await fetch(`${API_BASE_URL}/admin/facilities/${facilityId}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 성공 시 데이터 새로고침
      await fetchFacilities();
      await fetchDashboardStats();
      
      setShowApprovalModal(false);
      setActionReason('');
      setApprovalAction(null);
      setSelectedFacility(null);
    } catch (err) {
      console.error(`Failed to ${action} facility:`, err);
      setError(err instanceof Error ? err.message : `시설 ${action} 실패`);
    }
  };

  const handleFacilityClick = (facility: Facility) => {
    setSelectedFacility(facility);
    setShowDetailModal(true);
  };

  const handleActionClick = (action: 'approve' | 'reject' | 'suspend' | 'resume', facility: Facility) => {
    setSelectedFacility(facility);
    setApprovalAction(action);
    setShowApprovalModal(true);
  };

  const handleActionConfirm = () => {
    if (selectedFacility && approvalAction) {
      handleFacilityAction(approvalAction, selectedFacility.id, actionReason);
    }
  };

  const getApprovalStatusBadgeColor = (status: FacilityApprovalStatus) => {
    const statusColors: { [key: string]: string } = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      suspended: 'bg-gray-100 text-gray-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getOperationStatusBadgeColor = (status: FacilityOperationStatus) => {
    const statusColors: { [key: string]: string } = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-yellow-100 text-yellow-800',
      maintenance: 'bg-blue-100 text-blue-800',
      closed: 'bg-red-100 text-red-800',
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryDisplay = (category: FacilityCategory) => {
    const categoryNames: { [key: string]: string } = {
      studyroom: '독서실',
      academy: '학원',
      coworking: '코워킹',
      library: '도서관',
    };
    return categoryNames[category] || category;
  };

  const getApprovalStatusDisplay = (status: FacilityApprovalStatus) => {
    const statusNames: { [key: string]: string } = {
      pending: '승인 대기',
      approved: '승인 완료',
      rejected: '승인 거부',
      suspended: '운영 정지',
    };
    return statusNames[status] || status;
  };

  const getOperationStatusDisplay = (status: FacilityOperationStatus) => {
    const statusNames: { [key: string]: string } = {
      active: '정상 운영',
      inactive: '비활성화',
      maintenance: '점검 중',
      closed: '폐점',
    };
    return statusNames[status] || status;
  };

  const formatCurrency = (amount: number) => {
    return Math.round(amount / 10000).toLocaleString() + '만원';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR');
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return `${date.toLocaleDateString('ko-KR')} ${date.toLocaleTimeString(
      'ko-KR',
      {
        hour: '2-digit',
        minute: '2-digit',
      }
    )}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">시설 관리</h1>
              <p className="text-gray-600 mt-2">
                등록된 시설과 오너 정보를 관리합니다
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={fetchFacilities}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                새로고침
              </button>
            </div>
          </div>
        </div>

        {/* 통계 카드 */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">전체 시설</div>
              <div className="text-2xl font-bold text-gray-900">
                {stats.total_facilities}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">승인 완료</div>
              <div className="text-2xl font-bold text-green-600">
                {stats.approval_stats?.approved || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">승인 대기</div>
              <div className="text-2xl font-bold text-yellow-600">
                {stats.approval_stats?.pending || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">승인 거부</div>
              <div className="text-2xl font-bold text-red-600">
                {stats.approval_stats?.rejected || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">정상 운영</div>
              <div className="text-2xl font-bold text-green-600">
                {stats.operation_stats?.active || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">비활성화</div>
              <div className="text-2xl font-bold text-orange-600">
                {stats.operation_stats?.inactive || 0}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">월 매출</div>
              <div className="text-2xl font-bold text-purple-600">
                {formatCurrency(stats.monthly_revenue)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="text-sm font-medium text-gray-600">처리 대기</div>
              <div className="text-2xl font-bold text-indigo-600">
                {stats.pending_count}
              </div>
            </div>
          </div>
        )}

        {/* 필터링 및 검색 */}
        <div className="bg-white p-6 rounded-lg border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                검색
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="시설명, 주소, 연락처 검색"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                승인 상태
              </label>
              <select
                value={approvalStatusFilter}
                onChange={(e) => setApprovalStatusFilter(e.target.value as FacilityApprovalStatus | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 승인상태</option>
                <option value="pending">승인 대기</option>
                <option value="approved">승인 완료</option>
                <option value="rejected">승인 거부</option>
                <option value="suspended">운영 정지</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                운영 상태
              </label>
              <select
                value={operationStatusFilter}
                onChange={(e) => setOperationStatusFilter(e.target.value as FacilityOperationStatus | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 운영상태</option>
                <option value="active">정상 운영</option>
                <option value="inactive">비활성화</option>
                <option value="maintenance">점검 중</option>
                <option value="closed">폐점</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                시설 유형
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as FacilityCategory | '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">전체 유형</option>
                <option value="studyroom">독서실</option>
                <option value="academy">학원</option>
                <option value="coworking">코워킹</option>
                <option value="library">도서관</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                지역
              </label>
              <input
                type="text"
                placeholder="도시명 입력"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* 시설 목록 테이블 */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    시설정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    조직정보
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    운영현황
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    상태
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    등록일시
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    액션
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {facilities.map((facility) => (
                  <tr key={facility.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {facility.facility_name}
                        </div>
                        <div className="text-sm text-gray-500">
                          <BuildingOfficeIcon className="inline h-4 w-4 mr-1" />
                          {getCategoryDisplay(facility.category)}
                        </div>
                        <div className="text-sm text-gray-500">
                          <MapPinIcon className="inline h-4 w-4 mr-1" />
                          {facility.address}
                        </div>
                        {facility.phone && (
                          <div className="text-sm text-gray-500">
                            <PhoneIcon className="inline h-4 w-4 mr-1" />
                            {facility.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {facility.organization ? (
                          <>
                            <div className="text-sm font-medium text-gray-900">
                              {facility.organization.name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {facility.organization.business_type}
                            </div>
                            <div className="text-sm text-gray-500">
                              {facility.organization.plan_type}
                            </div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-500">조직 정보 없음</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm text-gray-900">
                          좌석: {facility.total_seats}석
                        </div>
                        <div className="text-sm text-gray-500">
                          이용자: {facility.total_users.toLocaleString()}명
                        </div>
                        <div className="text-sm text-gray-500">
                          매출: {formatCurrency(facility.monthly_revenue)}
                        </div>
                        <div className="text-sm text-gray-500">
                          현재 점유: {facility.current_occupancy}명
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getApprovalStatusBadgeColor(facility.approval_status)}`}
                        >
                          {getApprovalStatusDisplay(facility.approval_status)}
                        </span>
                        <br />
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getOperationStatusBadgeColor(facility.operation_status)}`}
                        >
                          {getOperationStatusDisplay(facility.operation_status)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(facility.created_at)}
                      </div>
                      {facility.last_activity && (
                        <div className="text-sm text-gray-500">
                          최종활동: {formatDateTime(facility.last_activity)}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleFacilityClick(facility)}
                          className="text-blue-600 hover:text-blue-900"
                          title="상세보기"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        
                        {/* 승인 대기 중인 시설에 대한 액션 버튼 */}
                        {facility.approval_status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleActionClick('approve', facility)}
                              className="text-green-600 hover:text-green-900"
                              title="승인"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleActionClick('reject', facility)}
                              className="text-red-600 hover:text-red-900"
                              title="거부"
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                          </>
                        )}
                        
                        {/* 승인된 시설에 대한 액션 버튼 */}
                        {facility.approval_status === 'approved' && (
                          <button
                            onClick={() => handleActionClick('suspend', facility)}
                            className="text-orange-600 hover:text-orange-900"
                            title="정지"
                          >
                            <ExclamationTriangleIcon className="h-5 w-5" />
                          </button>
                        )}
                        
                        {/* 정지된 시설에 대한 액션 버튼 */}
                        {facility.approval_status === 'suspended' && (
                          <button
                            onClick={() => handleActionClick('resume', facility)}
                            className="text-blue-600 hover:text-blue-900"
                            title="재개"
                          >
                            <CheckCircleIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* 페이지네이션 */}
        {totalCount > 0 && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              총 {totalCount}개 시설 중 {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)}개 표시
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage * pageSize >= totalCount}
                className="px-3 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          </div>
        )}

        {/* 시설이 없을 때 */}
        {!loading && facilities.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">조건에 맞는 시설이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 승인/거부/정지/재개 모달 */}
      {showApprovalModal && selectedFacility && approvalAction && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  시설 {approvalAction === 'approve' ? '승인' : approvalAction === 'reject' ? '거부' : approvalAction === 'suspend' ? '정지' : '재개'}
                </h3>
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  시설: <span className="font-medium">{selectedFacility.facility_name}</span>
                </p>
                <p className="text-sm text-gray-600">
                  현재 상태: <span className="font-medium">{getApprovalStatusDisplay(selectedFacility.approval_status)}</span>
                </p>
              </div>

              {(approvalAction === 'reject' || approvalAction === 'suspend') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    사유 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder={`${approvalAction === 'reject' ? '거부' : '정지'} 사유를 입력하세요`}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
              )}

              {(approvalAction === 'approve' || approvalAction === 'resume') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    메모 (선택사항)
                  </label>
                  <textarea
                    value={actionReason}
                    onChange={(e) => setActionReason(e.target.value)}
                    placeholder="관리자 메모를 입력하세요"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  취소
                </button>
                <button
                  onClick={handleActionConfirm}
                  disabled={(approvalAction === 'reject' || approvalAction === 'suspend') && !actionReason.trim()}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${
                    approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700' :
                    approvalAction === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    approvalAction === 'suspend' ? 'bg-orange-600 hover:bg-orange-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {approvalAction === 'approve' ? '승인' : approvalAction === 'reject' ? '거부' : approvalAction === 'suspend' ? '정지' : '재개'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 상세보기 모달 (향후 구현) */}
      {showDetailModal && selectedFacility && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">시설 상세 정보</h3>
              <button
                onClick={() => setShowDetailModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">기본 정보</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">시설명:</span> {selectedFacility.facility_name}</p>
                  <p><span className="font-medium">카테고리:</span> {getCategoryDisplay(selectedFacility.category)}</p>
                  <p><span className="font-medium">주소:</span> {selectedFacility.address}</p>
                  {selectedFacility.phone && <p><span className="font-medium">전화:</span> {selectedFacility.phone}</p>}
                  {selectedFacility.email && <p><span className="font-medium">이메일:</span> {selectedFacility.email}</p>}
                  {selectedFacility.website && <p><span className="font-medium">웹사이트:</span> {selectedFacility.website}</p>}
                </div>
              </div>
              
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-3">운영 정보</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">총 좌석:</span> {selectedFacility.total_seats}석</p>
                  <p><span className="font-medium">총 이용자:</span> {selectedFacility.total_users.toLocaleString()}명</p>
                  <p><span className="font-medium">현재 점유:</span> {selectedFacility.current_occupancy}명</p>
                  <p><span className="font-medium">월 매출:</span> {formatCurrency(selectedFacility.monthly_revenue)}</p>
                  <p><span className="font-medium">승인 상태:</span> {getApprovalStatusDisplay(selectedFacility.approval_status)}</p>
                  <p><span className="font-medium">운영 상태:</span> {getOperationStatusDisplay(selectedFacility.operation_status)}</p>
                </div>
              </div>
            </div>

            {selectedFacility.notes && (
              <div className="mt-6">
                <h4 className="text-md font-medium text-gray-900 mb-3">관리자 메모</h4>
                <p className="text-gray-700">{selectedFacility.notes}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FacilityManagement;
